/**
 * PollQuestionInput — the voter-side control for ONE poll question, plus the
 * pure answer-draft helpers PollCard drives it with.
 *
 * PR-6 (poll pipeline v2) turned the family card from "render questions[0]"
 * into "render an instance minted from a FormDefinition", so the per-question
 * rendering had to leave PollCard. The visual grammar is unchanged: the choice
 * / rating / free-text controls are lifted verbatim from the v1 card, and the
 * three new types (date / number / checkbox) reuse the app's existing typed-
 * field idiom from `components/intake/IntakeFieldInput` — same `<Input>`, same
 * required asterisk, same bounds handling.
 */
import { Input } from '@/components/ui/input';
import type { PollQuestion, VoteAnswer } from '@/types/polls';
import { digitsOnly, numericInputProps } from '@/components/editorial';
import { useLocale } from '@/i18n/useLocale';
import { localizeQuestion, pollStringKey } from './pollLocalization';
import { ShownInEnglish } from '@/components/apply/FormLanguageNotice';
import { useTranslation } from 'react-i18next';
import { msg, type Msg } from '@/i18n/msg';

/**
 * One question's in-progress answer. A flat record (rather than a union) keeps
 * PollCard's state a plain `Record<questionId, AnswerDraft>` — switching a
 * question's type is a mint-time concern, never a runtime one.
 *
 * `numberRaw` stays a string on purpose: `''` (untouched) and `0` (a real
 * answer) must not collapse into the same falsy value.
 */
export interface AnswerDraft {
  choiceId: string | null;
  choiceIds: string[];
  rating: number | null;
  text: string;
  date: string;
  numberRaw: string;
  checked: boolean;
}

export function emptyDraft(): AnswerDraft {
  return { choiceId: null, choiceIds: [], rating: null, text: '', date: '', numberRaw: '', checked: false };
}

/** A question is required unless it explicitly says otherwise (pre-PR-6 polls carry no flag). */
export function isRequired(q: PollQuestion): boolean {
  return q.required !== false;
}

/**
 * The draft as it would post — or `null` when the question is unanswered and
 * should be omitted from the vote payload entirely (the backend's
 * per-question `required` check counts presence, not content).
 *
 * CHECKBOX IS DELIBERATELY NEVER NULL. Mirroring
 * backend/src/routes/polls.js: "required means ANSWERED, not checked-true —
 * an unchecked box is data ('no'), not a refusal". A rendered checkbox always
 * has a determinate state, so it always carries an answer, and a required
 * checkbox therefore can never block a submit.
 */
export function draftToAnswer(q: PollQuestion, d: AnswerDraft): VoteAnswer | null {
  switch (q.type) {
    case 'single_choice':
      return d.choiceId ? { question_id: q._id, choice_ids: [d.choiceId] } : null;
    case 'multi_select':
      return d.choiceIds.length > 0 ? { question_id: q._id, choice_ids: d.choiceIds } : null;
    case 'rating':
      return d.rating !== null ? { question_id: q._id, rating: d.rating } : null;
    case 'free_text':
      return d.text.trim() ? { question_id: q._id, text: d.text.trim() } : null;
    case 'date':
      return d.date ? { question_id: q._id, date: d.date } : null;
    case 'number': {
      if (d.numberRaw.trim() === '') return null;
      const n = Number(d.numberRaw);
      return Number.isFinite(n) ? { question_id: q._id, number: n } : null;
    }
    case 'checkbox':
      return { question_id: q._id, checked: d.checked };
    default:
      return null;
  }
}

// #2109 R3 (fix pass): the messages are descriptors, resolved by the card at
// render, so a language switch re-renders them (R3-B3).
export const NEEDS_ANSWER: Msg = msg('community:poll.error.needsAnswer');

/**
 * The message this question would show if the family submitted now, or null.
 * Value errors (an out-of-range number) outrank the missing-answer message so
 * a filled-but-wrong field is never reported as blank.
 */
export function validateDraft(q: PollQuestion, d: AnswerDraft): Msg | null {
  if (q.type === 'number' && d.numberRaw.trim() !== '') {
    const n = Number(d.numberRaw);
    if (!Number.isFinite(n)) return msg('community:poll.error.number');
    // The bounds are the definition's, carried through the mint — the same
    // guard the vote route re-asserts server-side.
    if (q.min != null && n < q.min) return msg('community:poll.error.min', { min: q.min });
    if (q.max != null && n > q.max) return msg('community:poll.error.max', { max: q.max });
  }
  if (q.type === 'date' && d.date !== '' && Number.isNaN(new Date(d.date).getTime())) {
    return msg('community:poll.error.date');
  }
  if (isRequired(q) && draftToAnswer(q, d) === null) return NEEDS_ANSWER;
  return null;
}

interface Props {
  poll_id: string;
  question: PollQuestion;
  draft: AnswerDraft;
  onChange: (next: AnswerDraft) => void;
  /** The message to show, or null. PollCard withholds it until a submit attempt. */
  error: string | null;
  /** #2133: keys (`pollStringKey`) of the strings still in English on a card
   *  otherwise in the reader's language; each gets the "shown in English"
   *  note. Absent on an English or untranslated card. */
  englishKeys?: ReadonlySet<string>;
}

export default function PollQuestionInput({ poll_id, question, draft, onChange, error, englishKeys }: Props) {
  // #2109 R2: idempotent, so a caller that already localized loses nothing.
  const { locale } = useLocale();
  const { t } = useTranslation('community');
  const q = localizeQuestion(question, locale);
  const fieldId = `poll-${poll_id}-q-${q._id}`;
  const errorId = `${fieldId}-error`;
  const patch = (p: Partial<AnswerDraft>) => onChange({ ...draft, ...p });

  // Required fields get a rose asterisk (visual) + aria-required (assistive),
  // exactly as IntakeFieldInput does. Checkbox questions are excluded: their
  // "required" never gates anything, so an asterisk there would be a lie.
  const showRequiredMark = isRequired(q) && q.type !== 'checkbox';
  const a11y = {
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
    'aria-required': showRequiredMark || undefined,
  } as const;

  const mark = (key: string) => (englishKeys?.has(key) ? <ShownInEnglish /> : null);
  const prompt = (
    <>
      <span data-testid="poll-question-prompt">{q.prompt}</span>
      {mark(pollStringKey.prompt(q._id))}
    </>
  );
  // Choice / rating / free-text keep the v1 card's prompt paragraph verbatim so
  // a single-question poll renders byte-for-byte as it did before PR-6.
  const promptParagraph = (
    <p className="text-body font-medium mb-3">
      {prompt}
      {showRequiredMark && <span className="text-status-danger-fg ml-0.5" aria-hidden="true">*</span>}
    </p>
  );
  // The typed fields label their control instead, so the prompt IS the label.
  const promptLabel = (
    <label htmlFor={fieldId} className="block text-body font-medium mb-1.5">
      {prompt}
      {showRequiredMark && <span className="text-status-danger-fg ml-0.5" aria-hidden="true">*</span>}
    </label>
  );

  const errorNote = error ? (
    <p id={errorId} role="alert" className="mt-1 text-caption text-status-danger-fg">{error}</p>
  ) : null;

  let control: React.ReactNode = null;

  if (q.type === 'single_choice' && q.choices) {
    control = (
      <fieldset className="space-y-2">
        <legend className="sr-only">{q.prompt}</legend>
        {q.choices.map((c, i) => (
          <label key={c._id} className="flex items-center gap-2 text-body cursor-pointer">
            <input
              type="radio"
              // The FIRST control in the group carries the focus-target id.
              // PollCard moves focus to `poll-<id>-q-<qid>` when this is the
              // first invalid question; a group whose options had no id
              // swallowed that move, so the alert named a question the caller
              // was never taken to (WCAG 3.3.1 / 2.4.3).
              id={i === 0 ? fieldId : undefined}
              name={`poll-${poll_id}-q-${q._id}`}
              value={c._id}
              checked={draft.choiceId === c._id}
              onChange={() => patch({ choiceId: c._id })}
              className="accent-stevens-indigo"
            />
            {c.label}
            {mark(pollStringKey.choice(q._id, c._id))}
          </label>
        ))}
      </fieldset>
    );
  } else if (q.type === 'multi_select' && q.choices) {
    control = (
      <fieldset className="space-y-2">
        <legend className="sr-only">{q.prompt}</legend>
        {q.choices.map((c, i) => (
          <label key={c._id} className="flex items-center gap-2 text-body cursor-pointer">
            <input
              type="checkbox"
              // First control in the group carries the focus-target id — see
              // the single_choice branch above.
              id={i === 0 ? fieldId : undefined}
              value={c._id}
              checked={draft.choiceIds.includes(c._id)}
              onChange={(e) => patch({
                choiceIds: e.target.checked
                  ? [...draft.choiceIds, c._id]
                  : draft.choiceIds.filter((id) => id !== c._id),
              })}
              className="accent-stevens-indigo"
            />
            {c.label}
            {mark(pollStringKey.choice(q._id, c._id))}
          </label>
        ))}
      </fieldset>
    );
  } else if (q.type === 'rating') {
    control = (
      <div className="flex gap-2" role="group" aria-label={q.prompt}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            // First control in the group carries the focus-target id — see
            // the single_choice branch above.
            id={n === 1 ? fieldId : undefined}
            aria-label={String(n)}
            aria-pressed={draft.rating === n}
            onClick={() => patch({ rating: n })}
            className={[
              'w-9 h-9 rounded-md border text-body font-medium transition-colors',
              draft.rating === n
                ? 'bg-stevens-indigo-solid border-stevens-indigo-solid text-stevens-indigo-solid-fg'
                : 'border-border text-foreground hover:border-stevens-indigo',
            ].join(' ')}
          >
            {n}
          </button>
        ))}
      </div>
    );
  } else if (q.type === 'free_text') {
    control = (
      <textarea
        id={fieldId}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-body resize-none focus:outline-none focus:ring-2 focus:ring-stevens-indigo/40"
        rows={4}
        maxLength={q.max_text_length ?? 1000}
        placeholder={t('poll.answerPlaceholder')}
        value={draft.text}
        onChange={(e) => patch({ text: e.target.value })}
        aria-label={q.prompt}
        {...a11y}
      />
    );
  } else if (q.type === 'date') {
    control = (
      <Input
        id={fieldId}
        type="date"
        value={draft.date}
        onChange={(e) => patch({ date: e.target.value })}
        {...a11y}
      />
    );
  } else if (q.type === 'number') {
    control = (
      <Input
        id={fieldId}
        {...numericInputProps({
          decimal: true,
          max: q.max ?? undefined,
          signed: q.min != null && q.min < 0,
        })}
        value={draft.numberRaw}
        onChange={(e) =>
          patch({ numberRaw: digitsOnly(e.target.value, { decimal: true }) })
        }
        {...a11y}
      />
    );
  } else if (q.type === 'checkbox') {
    // IntakeFieldInput's checkbox idiom: the prompt IS the box's label, no
    // separate paragraph. Ticked is "yes", unticked is "no" — both are answers.
    return (
      <div>
        <label htmlFor={fieldId} className="flex items-start gap-2 text-body cursor-pointer">
          <input
            id={fieldId}
            type="checkbox"
            checked={draft.checked}
            onChange={(e) => patch({ checked: e.target.checked })}
            className="accent-stevens-indigo mt-0.5"
            aria-label={q.prompt}
            {...a11y}
          />
          <span className="font-medium">{prompt}</span>
        </label>
        {errorNote}
      </div>
    );
  }

  const usesLabel = q.type === 'date' || q.type === 'number';
  return (
    <div>
      {usesLabel ? promptLabel : promptParagraph}
      {control}
      {errorNote}
    </div>
  );
}
